import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useUserRole } from "@/hooks/use-user-role";

export type NotifItem = {
  id: string;
  title: string;
  subtitle?: string;
  date: string;
  url: string;
  kind: "payment" | "announcement";
};

const LS_KEY = "notif:lastSeen";

function readLastSeen(channel: string): number {
  if (typeof window === "undefined") return 0;
  try {
    const raw = window.localStorage.getItem(`${LS_KEY}:${channel}`);
    return raw ? Number(raw) || 0 : 0;
  } catch { return 0; }
}

function writeLastSeen(channel: string, ts: number) {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(`${LS_KEY}:${channel}`, String(ts)); } catch { /* ignore */ }
}

export function useNotifications() {
  const { role, isAdmin } = useUserRole();
  const isFinance = isAdmin || role === "bendahara";
  const [items, setItems] = useState<NotifItem[]>([]);
  const channel = isFinance ? "payments" : "staff_announcements";
  const [lastSeen, setLastSeen] = useState<number>(() => readLastSeen(channel));

  useEffect(() => {
    setLastSeen(readLastSeen(channel));
  }, [channel]);

  useEffect(() => {
    if (!role) return;
    let cancelled = false;

    const loadPayments = async () => {
      // Hanya verifikasi MANUAL yang perlu tindakan admin/bendahara.
      // Pembayaran otomatis (QRIS/VA/Bank via Pakasir) dikonfirmasi otomatis
      // oleh webhook, jadi jangan tampilkan di lonceng pemberitahuan.
      const { data } = await supabase
        .from("payments")
        .select("id, payment_name, bill_name, amount, created_at, updated_at, status, student_id, pakasir_order_id, payment_method")
        .eq("status", "waiting_verification")
        .is("pakasir_order_id", null)
        .order("updated_at", { ascending: false })
        .limit(20);
      if (cancelled) return;
      const list: NotifItem[] = (data ?? [])
        .filter((p) => p.payment_method !== "qris" && p.payment_method !== "pakasir")
        .map((p) => ({
          id: p.id,
          title: p.bill_name || p.payment_name || "Pembayaran baru",
          subtitle: p.amount ? `Rp ${Number(p.amount).toLocaleString("id-ID")} • Perlu dikonfirmasi` : "Perlu dikonfirmasi",
          date: p.updated_at || p.created_at,
          url: "/payments",
          kind: "payment",
        }));
      setItems(list);
    };

    const loadAnnouncements = async () => {
      const { data } = await supabase
        .from("staff_announcements")
        .select("id, title, content, date, created_at")
        .order("date", { ascending: false })
        .order("created_at", { ascending: false })
        .limit(20);
      if (cancelled) return;
      const list: NotifItem[] = (data ?? []).map((a) => ({
        id: a.id,
        title: a.title,
        subtitle: a.content?.slice(0, 80),
        date: a.date || a.created_at,
        url: "/staff-announcements",
        kind: "announcement",
      }));
      setItems(list);
    };

    const load = isFinance ? loadPayments : loadAnnouncements;
    load();

    const ch = supabase
      .channel(`notif-${channel}`)
      .on("postgres_changes", { event: "*", schema: "public", table: channel }, load)
      .subscribe();
    return () => { cancelled = true; supabase.removeChannel(ch); };
  }, [role, isFinance, channel]);

  const count = items.reduce(
    (n, it) => (new Date(it.date).getTime() > lastSeen ? n + 1 : n),
    0,
  );

  const markAllRead = () => {
    const ts = Date.now();
    writeLastSeen(channel, ts);
    setLastSeen(ts);
  };

  return { items, count, isFinance, markAllRead };
}

